import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

type ThemeMode = 'light';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode] = useState<ThemeMode>('light');

  useEffect(() => {
    localStorage.setItem('theme-mode', 'light');
  }, []);

  const toggleTheme = () => {
    console.log('Dark mode is disabled');
  };

  const setMode = () => {};

  // Sophisticated color palette
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#0A2540', // Deep navy
        light: '#1E3A5F',
        dark: '#051022',
      },
      secondary: {
        main: '#2A9D8F', // Muted teal - replaces gold
        light: '#4ECDC4',
        dark: '#1E7A6F',
      },
      background: {
        default: '#F8FAFC',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#0A2540',
        secondary: '#5B6F82',
      },
      success: {
        main: '#2A9D8F', // Teal for success
        light: '#4ECDC4',
        dark: '#1E7A6F',
      },
      warning: {
        main: '#E9B171', // Soft amber
        light: '#FFD966',
        dark: '#C68B4A',
      },
      error: {
        main: '#CD9777', // Terracotta
        light: '#E6B89C',
        dark: '#B0724A',
      },
      info: {
        main: '#84A98C', // Sage green
        light: '#A8C3B0',
        dark: '#5F7A67',
      },
      divider: 'rgba(10, 37, 64, 0.12)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 800 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      body1: { color: '#0A2540' },
      body2: { color: '#5B6F82' },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(10, 37, 64, 0.15)',
            },
          },
          containedPrimary: {
            background: 'linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)',
            color: '#FFFFFF',
            '&:hover': {
              background: 'linear-gradient(135deg, #1E3A5F 0%, #0A2540 100%)',
            },
          },
          containedSecondary: {
            background: 'linear-gradient(135deg, #2A9D8F 0%, #4ECDC4 100%)',
            color: '#FFFFFF',
            '&:hover': {
              background: 'linear-gradient(135deg, #4ECDC4 0%, #2A9D8F 100%)',
            },
          },
          outlined: {
            borderColor: '#0A2540',
            color: '#0A2540',
            '&:hover': {
              borderColor: '#2A9D8F',
              backgroundColor: 'rgba(42, 157, 143, 0.04)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6 },
          filled: {
            backgroundColor: '#F0F4F8',
            color: '#0A2540',
          },
          outlined: {
            borderColor: '#0A2540',
            color: '#0A2540',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: '#0A2540',
            borderBottom: '1px solid rgba(10, 37, 64, 0.12)',
          },
          head: {
            fontWeight: 700,
            backgroundColor: 'rgba(10, 37, 64, 0.02)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputLabel-root': { color: '#5B6F82' },
            '& .MuiOutlinedInput-root': {
              color: '#0A2540',
              '& fieldset': { borderColor: 'rgba(10, 37, 64, 0.23)' },
              '&:hover fieldset': { borderColor: '#0A2540' },
              '&.Mui-focused fieldset': { borderColor: '#2A9D8F' },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#0A2540',
            '&.Mui-checked': { color: '#2A9D8F' },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: '#0A2540',
            '&:hover': { backgroundColor: 'rgba(42, 157, 143, 0.04)' },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: '#0A2540',
            '&:hover': { color: '#2A9D8F' },
            '&.Mui-active': { color: '#2A9D8F' },
          },
          icon: { color: '#2A9D8F !important' },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
          standardSuccess: {
            backgroundColor: 'rgba(42, 157, 143, 0.1)',
            color: '#1E7A6F',
          },
          standardError: {
            backgroundColor: 'rgba(205, 151, 119, 0.1)',
            color: '#B0724A',
          },
          standardWarning: {
            backgroundColor: 'rgba(233, 177, 113, 0.1)',
            color: '#C68B4A',
          },
          standardInfo: {
            backgroundColor: 'rgba(132, 169, 140, 0.1)',
            color: '#5F7A67',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: '#0A2540',
            borderColor: 'rgba(10, 37, 64, 0.23)',
            '&.Mui-selected': {
              backgroundColor: 'rgba(42, 157, 143, 0.1)',
              color: '#2A9D8F',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: 'rgba(10, 37, 64, 0.08)' },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setMode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};